import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  PrivacyLinkError,
  PrivacyLinkProfile,
  fetchPrivacyLink,
  submitPrivacyLink,
} from '../services/privacyLinkService';

const GENDER_OPTIONS = [
  { value: '', label: 'Keine Angabe' },
  { value: 'male', label: 'Herr' },
  { value: 'female', label: 'Frau' },
  { value: 'diverse', label: 'Divers' },
];

const PrivacyLinkPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState<PrivacyLinkProfile | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [dplAccepted, setDplAccepted] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMsg('Ungültiger Link.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPrivacyLink(token);
        if (cancelled) return;
        if (data.status === 'already_submitted') {
          // Re-opened after a successful submit (happened live 22s after
          // confirming): that's a success story, not "Link ungültig".
          setAlreadySubmitted(true);
          setProfile(data);
          return;
        }
        setProfile(data);
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
        setGender(data.gender || '');
        setDplAccepted(Boolean(data.dpl_accepted));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PrivacyLinkError) {
          if (err.status === 410) {
            setErrorMsg('Dieser Link ist abgelaufen oder wurde bereits verwendet.');
          } else if (err.status === 404) {
            setErrorMsg('Link nicht gefunden.');
          } else {
            setErrorMsg(err.detail || 'Link konnte nicht geladen werden.');
          }
        } else {
          setErrorMsg('Verbindungsfehler. Bitte später erneut versuchen.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg('Vorname und Nachname dürfen nicht leer sein.');
      return;
    }
    if (!dplAccepted) {
      setErrorMsg('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    setErrorMsg(null);
    setSubmitting(true);
    try {
      await submitPrivacyLink(token, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender: gender || null,
        dpl_accepted: true,
      });
      setSuccess(true);
    } catch (err) {
      if (err instanceof PrivacyLinkError) {
        setErrorMsg(err.detail || 'Speichern fehlgeschlagen.');
      } else {
        setErrorMsg('Verbindungsfehler. Bitte später erneut versuchen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-success-strong" />
              Bereits bestätigt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {profile?.business_name
                ? `Ihre Zustimmung liegt ${profile.business_name} bereits vor.`
                : 'Ihre Zustimmung liegt bereits vor.'}{' '}
              Es ist nichts weiter zu tun — Sie können dieses Fenster schließen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Link ungültig</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMsg ?? 'Unbekannter Fehler.'}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-success-strong" />
              Vielen Dank!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Ihre Angaben wurden gespeichert. Sie können dieses Fenster nun schließen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Profil bestätigen</CardTitle>
          {profile.business_name && (
            <p className="text-sm text-muted-foreground">
              {profile.business_name}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">Vorname</Label>
              <Input
                id="first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Nachname</Label>
              <Input
                id="last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">Anrede</Label>
              <select
                id="gender"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={submitting}
              >
                {GENDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id="dpl"
                checked={dplAccepted}
                onCheckedChange={(checked) => setDplAccepted(checked === true)}
                disabled={submitting}
              />
              <Label htmlFor="dpl" className="text-sm leading-snug font-normal cursor-pointer">
                Ich stimme der{' '}
                {profile.privacy_policy_url ? (
                  <a
                    href={profile.privacy_policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-strong underline"
                  >
                    Datenschutzerklärung
                  </a>
                ) : (
                  <span className="text-primary-strong underline">Datenschutzerklärung</span>
                )}
                {' '}zu.
              </Label>
            </div>

            {errorMsg && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMsg}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Bestätigen'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyLinkPage;
